import { useMemo, useState } from 'react';

import type { RoleId, UserMembershipDto } from '../types/user';
import { roleCatalog } from '../types/user';

export function EditRolesDialog({
  membership,
  protectCustomerAdmin,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  membership: UserMembershipDto;
  protectCustomerAdmin: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (roles: RoleId[]) => void;
}) {
  const initial = useMemo(() => membership.role_ids.filter((role): role is RoleId => roleCatalog.some((item) => item.id === role)), [membership.role_ids]);
  const [roles, setRoles] = useState<RoleId[]>(initial.length ? initial : ['viewer']);

  function toggle(roleId: RoleId) {
    if (protectCustomerAdmin && roleId === 'customer_admin') return;
    setRoles((current) => current.includes(roleId) ? current.filter((item) => item !== roleId) : [...current, roleId]);
  }

  return (
    <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="edit-user-roles-title">
      <button className="r42-dialog-backdrop" aria-label="Close role editor" onClick={busy ? undefined : onClose} />
      <form
        className="r42-dialog-card r42-user-dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (roles.length && !busy) onSubmit(roles);
        }}
      >
        <span className="r42-eyebrow">Access</span>
        <h2 id="edit-user-roles-title">Edit roles</h2>
        <p>Role changes apply only to <strong>{membership.email}</strong> in this organization.</p>

        <div className="r42-role-options">
          {roleCatalog.map((role) => {
            const checked = roles.includes(role.id);
            const locked = protectCustomerAdmin && role.id === 'customer_admin';
            return (
              <label key={role.id} className={`r42-role-option${checked ? ' selected' : ''}${locked ? ' locked' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(role.id)} disabled={busy || locked} />
                <span><strong>{role.label}</strong><small>{role.description}</small></span>
              </label>
            );
          })}
        </div>

        {protectCustomerAdmin ? <p className="r42-user-protection-note"><i className="bi bi-shield-check" aria-hidden="true" /> Route42 requires at least one active Customer Administrator, so this role cannot be removed from the last active administrator.</p> : null}
        {error ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Roles could not be updated.</strong><span>{error}</span></div></div> : null}

        <div className="r42-dialog-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn r42-btn-primary" disabled={!roles.length || busy}>
            {busy ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Saving…</> : 'Save roles'}
          </button>
        </div>
      </form>
    </div>
  );
}
